import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import MediaCard from '@/components/feature/MediaCard';
import TagFilter from '@/components/feature/TagFilter';
import { mediaList } from '@/mocks/media';
import { categoryMeta, genreOptions, ratingOptions, regionOptions, yearOptions } from '@/mocks/filters';
import { DEFAULT_FILTER, filterMedia, mediaByType, sortMedia, type FilterState } from '@/utils/media';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import type { MediaType } from '@/types/media';

const LOAD_STEP = 6;
const VALID_TYPES: MediaType[] = ['movie', 'tv', 'variety'];

export default function CategoryFilterPage() {
  const { type } = useParams<{ type: string }>();
  const validType = VALID_TYPES.includes(type as MediaType);
  const meta = categoryMeta[(type as MediaType) ?? 'movie'];

  const [draft, setDraft] = useState<FilterState>(DEFAULT_FILTER);
  const [applied, setApplied] = useState<FilterState>(DEFAULT_FILTER);
  const [visible, setVisible] = useState(LOAD_STEP);

  useDocumentTitle(
    validType ? `${meta.title}筛选 - 蘑菇视频` : '筛选 - 蘑菇视频',
    validType ? `按类型、地区、年份与评分区间筛选${meta.name}内容，快速找到想看的影视作品。` : '影视内容筛选。',
  );

  const results = useMemo(() => {
    if (!validType) return [];
    const base = mediaByType(mediaList, type as MediaType);
    const filtered = filterMedia(base, applied);
    return sortMedia(filtered, 'hot');
  }, [type, validType, applied]);

  const visibleItems = results.slice(0, visible);

  const apply = () => {
    setApplied(draft);
    setVisible(LOAD_STEP);
  };

  const reset = () => {
    setDraft(DEFAULT_FILTER);
    setApplied(DEFAULT_FILTER);
    setVisible(LOAD_STEP);
  };

  const setField = (key: keyof FilterState) => (value: string) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  if (!validType) {
    return (
      <div className="w-full max-w-content mx-auto px-4 md:px-8 py-16">
        <EmptyState
          title="未找到该分类"
          description="你访问的分类不存在，返回首页看看其他精彩内容吧。"
          icon="ri-error-warning-line"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  return (
    <div className="w-full max-w-content mx-auto px-4 md:px-8 py-6">
      <Breadcrumb items={[{ label: meta.name, to: `/category/${type}` }, { label: '高级筛选' }]} />

      <h1 className="mt-4 text-2xl md:text-3xl font-black text-foreground-950">
        {meta.name}高级筛选
      </h1>
      <p className="mt-2 text-sm text-foreground-500">组合类型、地区、年份与评分区间，精准找到想看的内容。</p>

      <section className="mt-6 rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
        <div className="flex flex-col gap-4">
          <TagFilter label="类型" options={genreOptions} value={draft.genre} onChange={setField('genre')} />
          <TagFilter label="地区" options={regionOptions} value={draft.region} onChange={setField('region')} />
          <TagFilter label="年份" options={yearOptions} value={draft.year} onChange={setField('year')} />
          <TagFilter label="评分" options={ratingOptions} value={draft.rating} onChange={setField('rating')} />
        </div>
        <div className="mt-5 pt-5 border-t border-background-200 flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={apply}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-check-line text-lg"></i>
            确认筛选
          </button>
          <button
            type="button"
            onClick={reset}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-background-100 border border-background-200 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-refresh-line text-lg"></i>
            重置条件
          </button>
          <Link
            to={`/category/${type}`}
            className="ml-auto text-sm text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            返回 {meta.name} 分类
          </Link>
        </div>
      </section>

      <section className="mt-6">
        <p className="text-sm text-foreground-500 mb-5">
          筛选结果：共 <strong className="text-primary-600 font-semibold">{results.length}</strong> 部内容
        </p>

        {results.length === 0 ? (
          <EmptyState
            title="暂无相关内容"
            description="当前筛选条件下没有匹配的影视内容，试试放宽筛选条件或重置。"
            icon="ri-filter-off-line"
            actionLabel="重置筛选"
            onAction={reset}
          />
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {visibleItems.map((item) => (
                <MediaCard key={item.id} item={item} />
              ))}
            </div>
            {visible < results.length && (
              <div className="flex justify-center mt-8">
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + LOAD_STEP)}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md border border-background-300 text-foreground-700 text-sm font-medium hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-add-line text-lg"></i>
                  加载更多（剩余 {results.length - visible}）
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}